import React, { useRef, useEffect } from 'react'
import { ReactTable } from '../reactTable'
import { ReactTableProps } from '../reactTable/interface'
import { CursorPagination } from '../../../../types'
import LoaderGif from 'uiComponents/loaders/loader.gif'
import BaseKnowledgeLink from 'uiComponents/typography/BaseKnowledgeLink'
import './infiniteTable.scss'

export interface InfiniteTableProps<D extends object> extends Omit<ReactTableProps<D>, 'pagination' | 'loadData'> {
    loading?: boolean
    loadingMore: boolean
    pagination: CursorPagination
    onLoadMore: () => void
    loadingMessage?: string
    knowledgeLink?: string
}

export const InfiniteTable = <D extends object>({
    data,
    columns,
    loadingMore,
    pagination,
    onLoadMore,
    loadingMessage = 'Loading more...',
    knowledgeLink,
    loading,
    ...restProps
}: InfiniteTableProps<D>) => {
    const observerTarget = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries
                if (entry.isIntersecting && pagination.next && !loadingMore) {
                    onLoadMore()
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1,
            },
        )

        const currentTarget = observerTarget.current
        if (currentTarget) {
            observer.observe(currentTarget)
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget)
            }
            observer.disconnect()
        }
    }, [pagination.next, loadingMore, onLoadMore])

    return (
        <div className="infinite-table-wrapper">
            <ReactTable {...restProps} data={data} columns={columns} pagination={pagination} loading={loading} />
            {knowledgeLink && <BaseKnowledgeLink link={knowledgeLink} />}
            <div ref={observerTarget} className="loader-trigger">
                {!!data.length && (
                    <div className="loading-indicator">
                        {loadingMore ? (
                            <>
                                <img className="spinner" src={LoaderGif} />
                                {loadingMessage}
                            </>
                        ) : !pagination.next ? (
                            'No more results'
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    )
}
